import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebaseService';

export interface PatternPreviewMetrics {
  originalLength: number;
  processedLength: number;
  reductionPercentage: number;
  timeElapsedMs: number;
}

export interface PatternPreviewResult {
  success: boolean;
  originalHtml: string;
  processedHtml: string;
  sanitizedOriginal: string;
  sanitizedProcessed: string;
  metrics: PatternPreviewMetrics;
  error?: string;
  message: string;
}

/**
 * Calls the Cloud Function to preview the impact of a pattern on a specific URL
 */
export const previewPatternOnUrl = async (
  url: string,
  organizationId: string,
  locationId: string,
  pattern: string,
  description: string
): Promise<PatternPreviewResult> => {
  try {
    console.log('[Pattern Preview] Starting pattern preview for:', url);
    
    // Initialize Firebase Functions with us-east4 region
    const functions = getFunctions(app, "us-east4");
    const previewPattern = httpsCallable(functions, 'previewPatternOnUrl');

    // Call the Cloud Function with all parameters
    const result = await previewPattern({
      url,
      organizationId,
      locationId,
      pattern,
      description
    });

    const data = result.data as PatternPreviewResult;

    console.log(`[Pattern Preview] Cloud Function response:`, {
      success: data.success,
      originalLength: data.metrics?.originalLength,
      processedLength: data.metrics?.processedLength,
      reductionPercentage: data.metrics?.reductionPercentage,
      timeElapsed: data.metrics?.timeElapsedMs
    });

    if (data.success) {
      console.log(`[Pattern Preview] Preview successful`);
      console.log(`[Pattern Preview] Original HTML: ${data.originalHtml.length} chars`);
      console.log(`[Pattern Preview] Processed HTML: ${data.processedHtml.length} chars`);
      
      return {
        success: true,
        originalHtml: data.originalHtml,
        processedHtml: data.processedHtml,
        sanitizedOriginal: data.sanitizedOriginal,
        sanitizedProcessed: data.sanitizedProcessed,
        metrics: data.metrics,
        message: data.message
      };
    } else {
      return {
        success: false,
        originalHtml: '',
        processedHtml: '',
        sanitizedOriginal: '',
        sanitizedProcessed: '',
        metrics: {
          originalLength: 0,
          processedLength: 0,
          reductionPercentage: 0,
          timeElapsedMs: 0
        },
        error: data.error || 'Pattern preview failed',
        message: data.message
      };
    }

  } catch (error) {
    console.error(`[Pattern Preview] Error calling Cloud Function:`, error);
    return {
      success: false,
      originalHtml: '',
      processedHtml: '',
      sanitizedOriginal: '',
      sanitizedProcessed: '',
      metrics: {
        originalLength: 0,
        processedLength: 0,
        reductionPercentage: 0,
        timeElapsedMs: 0
      },
      error: error instanceof Error ? error.message : 'Failed to preview pattern',
      message: error instanceof Error ? error.message : 'An unknown error occurred'
    };
  }
}; 